import { Box, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import 'moment/locale/en-gb';
import {
  Country,
  Division,
  Region,
  ReportingPeriod,
  ReportingPeriodGroup,
  Site,
  Subdivision
} from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  ExportRequestParameters,
  ExportParametersProps
} from '../ExportDataButton';
import { values } from '../../../util/general_utils';
import { SiteExtended } from '../../../lib/metric_capture/site';
import { MetricExtended } from '../../../lib/metric_capture/metric';
import ReportingPeriodGroupSelect from '../../shared/input/select/ReportingPeriodGroupSelect';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import ReportingPeriodSelect from '../../shared/input/select/ReportingPeriodSelect';
import MetricSelect from '../../shared/input/select/MetricSelect';
import DivisionSelect from '../../shared/input/select/DivisionSelect';
import SubdivisionSelect from '../../shared/input/select/SubdivisionSelect';
import SiteSelect from '../../shared/input/select/SiteSelect';
import CountrySelect from '../../shared/input/select/CountrySelect';
import RegionSelect from '../../shared/input/select/RegionSelect';
import SkeletonGrid, { SkeletonItem } from '../../shared/loading/SkeletonGrid';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import {
  MetricRecordEntitiesMap,
  getMetricRecordEntitiesMap
} from '../../../graphql/services/metric_record.service';

const SOURCE = 'ESGDataExportParameters';

// Data sent in request to unique Cloud Function.
interface ESGDataExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  reporting_period_group_id: string;
  reporting_period_ids: Array<string>;
  metric_ids?: Array<string>;
  country_ids?: Array<string>;
  region_ids?: Array<string>;
  division_ids?: Array<string>;
  subdivision_ids?: Array<string>;
  site_ids?: Array<string>;
}

/**
 * Reusable importing context for metric record file data containing relevant inputs for the microservice.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const ESGDataExportParameters = ({ export_type }: ExportParametersProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  // Default parameters state for inputs.
  const initial_parameters: ESGDataExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    reporting_period_group_id: '',
    reporting_period_ids: [],
    metric_ids: undefined,
    country_ids: undefined,
    region_ids: undefined,
    division_ids: undefined,
    subdivision_ids: undefined,
    site_ids: undefined
  };

  const initial_entities_map: MetricRecordEntitiesMap = {
    countries: {},
    regions: {},
    divisions: {},
    subdivisions: {},
    sites: {},
    reporting_period_groups: {},
    reporting_periods: {},
    metrics: {},
    emission_factors: {},
    external_companies: {}
  };

  const [esgDataExportParameters, setESGDataExportParameters] =
    useState<ESGDataExportRequestParameters>(initial_parameters);
  const [entitiesMap, setEntitiesMap] = useState<MetricRecordEntitiesMap>(initial_entities_map);
  const [selectedReportingPeriodGroup, setSelectedReportingPeriodGroup] =
    useState<ReportingPeriodGroup | null>(null);
  const [selectedReportingPeriods, setSelectedReportingPeriods] = useState<Array<ReportingPeriod>>(
    []
  );
  const [selectedMetrics, setSelectedMetrics] = useState<Array<MetricExtended>>([]);
  const [selectedCountries, setSelectedCountries] = useState<Array<Country>>([]);
  const [selectedRegions, setSelectedRegions] = useState<Array<Region>>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Array<Division>>([]);
  const [selectedSubdivisions, setSelectedSubdivisions] = useState<Array<Subdivision>>([]);
  const [selectedSites, setSelectedSites] = useState<Array<SiteExtended>>([]);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(true);

  // Validation check for unique export parameters.
  const parameters_valid = Boolean(
    selectedReportingPeriodGroup && !!selectedReportingPeriods.length
  );

  const reporting_periods = values<ReportingPeriod>(entitiesMap.reporting_periods);
  const sorted_metrics: Array<MetricExtended> = values<MetricExtended>(entitiesMap.metrics).sort(
    (a: MetricExtended, b: MetricExtended) => a.name.localeCompare(b.name)
  );

  const handleReportingPeriodGroupChange = (
    reporting_period_group: ReportingPeriodGroup | null
  ): void => {
    // Set reporting periods automatically per reporting period group change.
    const reporting_period_for_group: Array<ReportingPeriod> = reporting_period_group
      ? reportingPeriodFilterByGroup(reporting_period_group.id, reporting_periods)
      : [];

    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      reporting_period_group_id: reporting_period_group ? reporting_period_group.id : '',
      reporting_period_ids: reporting_period_for_group.map(
        (reporting_period: ReportingPeriod) => reporting_period.id
      )
    };
    setSelectedReportingPeriodGroup(reporting_period_group);
    setSelectedReportingPeriods(reporting_period_for_group);
    setESGDataExportParameters(updated_parameters);
  };

  const handleReportingPeriodsChange = (reporting_periods: Array<ReportingPeriod>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      reporting_period_ids: reporting_periods.map(
        (reporting_period: ReportingPeriod) => reporting_period.id
      )
    };
    setSelectedReportingPeriods(reporting_periods);
    setESGDataExportParameters(updated_parameters);
  };

  const handleMetricsChange = (metrics: Array<MetricExtended>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      metric_ids: metrics.length ? metrics.map((metric: MetricExtended) => metric.id) : undefined
    };
    setSelectedMetrics(metrics);
    setESGDataExportParameters(updated_parameters);
  };

  const handleCountriesChange = (countries: Array<Country>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      country_ids: countries.length ? countries.map((country: Country) => country.id) : undefined,
      region_ids: undefined
    };
    setSelectedCountries(countries);
    setSelectedRegions([]);
    setESGDataExportParameters(updated_parameters);
  };

  const handleRegionsChange = (regions: Array<Region>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      region_ids: regions.length ? regions.map((region: Region) => region.id) : undefined
    };
    setSelectedRegions(regions);
    setESGDataExportParameters(updated_parameters);
  };

  const handleDivisionsChange = (divisions: Array<Division>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      division_ids: divisions.length
        ? divisions.map((division: Division) => division.id)
        : undefined,
      subdivision_ids: undefined,
      site_ids: undefined
    };
    setSelectedDivisions(divisions);
    setSelectedSubdivisions([]);
    setSelectedSites([]);
    setESGDataExportParameters(updated_parameters);
  };

  const handleSubdivionsChange = (subdivisions: Array<Subdivision>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      subdivision_ids: subdivisions.length
        ? subdivisions.map((subdivision: Subdivision) => subdivision.id)
        : undefined,
      site_ids: undefined
    };
    setSelectedSubdivisions(subdivisions);
    setSelectedSites([]);
    setESGDataExportParameters(updated_parameters);
  };

  const handleSitesChange = (sites: Array<SiteExtended>): void => {
    const updated_parameters: ESGDataExportRequestParameters = {
      ...esgDataExportParameters,
      site_ids: sites.length ? sites.map((site: SiteExtended) => site.id) : undefined
    };
    setSelectedSites(sites);
    setESGDataExportParameters(updated_parameters);
  };

  // Fetch entities needed for capture parameter inputs pre metric record grid management.
  const fetchMetricRecordEntitiesMap = async () => {
    setLoadingEntities(true);
    try {
      if (group && company && user) {
        const entities_map: MetricRecordEntitiesMap = await getMetricRecordEntitiesMap(
          group.id,
          company.id,
          user
        );
        if (entities_map) {
          setEntitiesMap({
            countries: entities_map.countries,
            regions: entities_map.regions,
            divisions: entities_map.divisions,
            subdivisions: entities_map.subdivisions,
            sites: entities_map.sites,
            reporting_period_groups: entities_map.reporting_period_groups,
            reporting_periods: entities_map.reporting_periods,
            metrics: entities_map.metrics,
            emission_factors: entities_map.emission_factors,
            external_companies: entities_map.external_companies
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch capture entities. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  useEffect(() => {
    (async () => {
      setESGDataExportParameters(initial_parameters);
      setSelectedReportingPeriodGroup(null);
      setSelectedReportingPeriods([]);
      setSelectedCountries([]);
      setSelectedRegions([]);
      setSelectedDivisions([]);
      setSelectedSubdivisions([]);
      setSelectedSites([]);
      setEntitiesMap(initial_entities_map);
      if (group && company && user) {
        await fetchMetricRecordEntitiesMap();
      }
    })();
  }, [user, company, group]);

  const skeleton_grid_items: Array<SkeletonItem> = Array(1).fill({
    width: 350,
    height: 50,
    variant: 'rounded'
  });

  return loadingEntities ? (
    <SkeletonGrid skeleton_items={skeleton_grid_items} />
  ) : (
    <Grid container spacing={4}>
      <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
        <Box width={350}>
          <ReportingPeriodGroupSelect
            reporting_period_group_options={values<ReportingPeriodGroup>(
              entitiesMap.reporting_period_groups
            )}
            selected_reporting_period_group={selectedReportingPeriodGroup}
            handleChangeReportingPeriodGroups={(
              reporting_period_group: Array<ReportingPeriodGroup> | ReportingPeriodGroup | null
            ) => {
              if (!Array.isArray(reporting_period_group)) {
                handleReportingPeriodGroupChange(reporting_period_group);
              }
            }}
          />
        </Box>
        {selectedReportingPeriodGroup && (
          <Box width={350}>
            <ReportingPeriodSelect
              disable_selected_tags
              allow_multi_select
              reporting_period_options={values<ReportingPeriod>(
                entitiesMap.reporting_periods
              ).filter(
                (reporting_period: ReportingPeriod) =>
                  selectedReportingPeriodGroup &&
                  reporting_period.reporting_period_group?.id === selectedReportingPeriodGroup.id
              )}
              selected_reporting_periods={selectedReportingPeriods}
              handleChangeReportingPeriods={(
                reporting_periods: Array<ReportingPeriod> | ReportingPeriod | null
              ) => {
                if (Array.isArray(reporting_periods)) {
                  handleReportingPeriodsChange(reporting_periods);
                }
              }}
            />
          </Box>
        )}
      </Grid>
      {selectedReportingPeriodGroup && (
        <>
          <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
            <Box width={350}>
              <CountrySelect
                disable_selected_tags
                multi_select
                options={values<Country>(entitiesMap.countries)}
                selected_options={selectedCountries}
                variant="standard"
                handleChangeCountries={(countries: Array<Country> | Country | null) => {
                  if (Array.isArray(countries)) {
                    handleCountriesChange(countries);
                  }
                }}
              />
            </Box>
            <Box width={350}>
              <RegionSelect
                disable_selected_tags
                multi_select
                options={values<Region>(entitiesMap.regions).filter(
                  (region: Region) =>
                    !esgDataExportParameters.country_ids?.length ||
                    esgDataExportParameters.country_ids.includes(region.country.id)
                )}
                selected_options={selectedRegions}
                handleChangeRegions={(regions: Array<Region> | Region | null) => {
                  if (Array.isArray(regions)) {
                    handleRegionsChange(regions);
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
            <Box width={350}>
              <DivisionSelect
                disable_selected_tags
                multi_select
                options={values<Division>(entitiesMap.divisions)}
                selected_options={selectedDivisions}
                handleChangeDivisions={(divisions: Array<Division> | Division | null) => {
                  if (Array.isArray(divisions)) {
                    handleDivisionsChange(divisions);
                  }
                }}
              />
            </Box>
            <Box width={350}>
              <SubdivisionSelect
                disable_selected_tags
                multi_select
                options={values<Subdivision>(entitiesMap.subdivisions).filter(
                  (subdivision: Subdivision) =>
                    !esgDataExportParameters.division_ids?.length ||
                    esgDataExportParameters.division_ids.includes(subdivision.division.id)
                )}
                selected_options={selectedSubdivisions}
                handleChangeSubdivisions={(
                  subdivisions: Array<Subdivision> | Subdivision | null
                ) => {
                  if (Array.isArray(subdivisions)) {
                    handleSubdivionsChange(subdivisions);
                  }
                }}
              />
            </Box>
            <Box width={350}>
              <SiteSelect
                disable_selected_tags
                multi_select
                options={values<SiteExtended>(entitiesMap.sites).filter(
                  (site: SiteExtended) =>
                    (!esgDataExportParameters.subdivision_ids?.length ||
                      esgDataExportParameters.subdivision_ids.includes(site.subdivision.id)) &&
                    (!esgDataExportParameters.division_ids?.length ||
                      esgDataExportParameters.division_ids.includes(site.division.id))
                )}
                selected_options={selectedSites}
                handleChangeSites={(
                  sites: Array<SiteExtended | Site> | SiteExtended | Site | null
                ) => {
                  if (Array.isArray(sites)) {
                    handleSitesChange(sites as Array<SiteExtended>);
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Box width={350}>
              <MetricSelect
                disable_selected_tags
                multi_select
                options={sorted_metrics}
                group_by="metric_group.name"
                selected_options={selectedMetrics}
                handleChangeMetrics={(metrics: Array<MetricExtended> | MetricExtended | null) => {
                  if (Array.isArray(metrics)) {
                    handleMetricsChange(metrics);
                  }
                }}
              />
            </Box>
          </Grid>
        </>
      )}
      <Grid size={12} sx={{ textAlign: 'left' }}>
        <ExportDataButton
          user={user}
          export_type={export_type}
          parameters={esgDataExportParameters}
          disabled={!parameters_valid}
          label="Export"
        />
      </Grid>
    </Grid>
  );
};

export default ESGDataExportParameters;
